import { RequestModelInterface } from "@/service/models/request-model-interface";
import { XmlDecodingException } from "@/utilities/exceptions/xml-decoding-exception";
import { XmlDocumentWrapper } from "./xml-document-wrapper";

export class XmlElementWrapper
{
    protected elem: Element;
    protected langCode: string;
    protected fallbackLangCode: string;

    public constructor(elem: Element, langCode: string, fallbackLangCode: string)
    {
        this.elem = elem;

        this.langCode = langCode;
        this.fallbackLangCode = fallbackLangCode;
    }

    public get element(): Element
    {
        return this.elem;
    }

    public get textContent(): string|null
    {
        if (this.elem.textContent === null) {
            return null;
        }

        return this.elem.textContent.trim();
    }

    public get ownerDocument(): XmlDocumentWrapper
    {
        return new XmlDocumentWrapper(this.elem.ownerDocument);
    }


    //
    // XML encoding helpers
    //

    /**
     * Forward the native append method to the XML element object.
     * 
     * @param child         The child element to append
     */
    public append(child: XmlElementWrapper): void
    {
        this.elem.append(child.elem);
    }

    /**
     * Recursively encodes each element in an array of models and appends
     * them to the XML element.
     * 
     * @param children      The models to encode and append
     */
    public appendChildren(children: RequestModelInterface[]): void
    {
        for (const child of children) {
            this.elem.appendChild(child.getXml(this.ownerDocument).element);
        }
    }

    /**
     * Creates a new XML element and appends it to the current one.
     * 
     * @param childTagName  Tag name of the element to create
     * @param content       Text value of the element to create
     * @returns             The newly created element
     */
    public appendNewElement(childTagName: string, content: string|null = null): XmlElementWrapper
    {
        const newElement = this.elem.ownerDocument.createElement(childTagName);

        if (content !== null) {
            newElement.textContent = content;
        }

        this.elem.appendChild(newElement);

        return new XmlElementWrapper(newElement, this.langCode, this.fallbackLangCode);
    }


    //
    // XML decoding helpers
    //

    /**
     * Get the first direct-child element with the given tagName, or null if there is none.
     * 
     * @param tagName       The tag name to search for
     * @returns             The 1st matching element, or null if none was found
     */
    public firstChildByTagOrNull(tagName: string): XmlElementWrapper|null
    {
        return this.firstChildWithTagInOrNull([tagName]);
    }

    /**
     * Get the first direct-child element with the given tagName, throws an exception if
     * there is none.
     * 
     * @param tagName       The tag name to search for
     * @returns             The 1st matching element
     */
    public firstChildByTag(tagName: string): XmlElementWrapper
    {
        const foundElem = this.firstChildByTagOrNull(tagName);

        if (foundElem === null) {
            throw XmlDecodingException.requiredChildNotFound(this.elem, tagName);
        }

        return foundElem;
    }

    /**
     * Get the first direct-child element which has a tagName matching one of multiple potential
     * names, or null if there is none.
     * 
     * @param tagNames      The list of tag names to search for
     * @returns             The 1st matching element, or null if none was found
     */
    public firstChildWithTagInOrNull(tagNames: string[]): XmlElementWrapper|null
    {
        for (const subElem of this.elem.children) {
            if (tagNames.includes(subElem.tagName)) {
                return new XmlElementWrapper(subElem, this.langCode, this.fallbackLangCode);
            }
        }

        return null;
    }

    /**
     * Get the first direct-child element which has a tagName matching one of multiple potential
     * names. Throws an exception if there is none.
     * 
     * @param tagNames      The list of tag names to search for
     * @returns             The 1st matching element
     */
    public firstChildWithTagIn(tagNames: string[]): XmlElementWrapper
    {
        const foundElem = this.firstChildWithTagInOrNull(tagNames);

        if (foundElem === null) {
            throw XmlDecodingException.noRequiredChildFound(this.elem, tagNames);
        }

        return foundElem;
    }

    /**
     * Get the last element anywhere in the children hierarchy that has the given tag name.
     * Returns null if no matching element can be found.
     * 
     * @param tagName       The tag name to search for
     * @returns             The last matching element or null if there is none
     */
    public lastElementInsideByTagOrNull(tagName: string): XmlElementWrapper|null
    {
        const foundElements = this.elem.getElementsByTagName(tagName);

        if (foundElements.length === 0) {
            return null;
        }

        return new XmlElementWrapper(foundElements[foundElements.length - 1], this.langCode, this.fallbackLangCode);
    }

    /**
     * Get the last element anywhere in the children hierarchy that has the given tag name.
     * Throws an exception if no matching element can be found.
     * 
     * @param tagName       The tag name to search for
     * @returns             The last matching element
     */
    public lastElementInsideByTag(tagName: string): XmlElementWrapper
    {
        const lastElementOrNull = this.lastElementInsideByTagOrNull(tagName);

        if (lastElementOrNull === null) {
            throw XmlDecodingException.elementNotFoundInside(this.elem, tagName);
        }

        return lastElementOrNull;
    }

    /**
     * Calls a callback for every child element matching the given tag names.
     * 
     * @param childTagName  The tag names to filter children with
     * @param callback      A callback executed for each matching element
     */
    public forEachChildWithTagIn(childTagName: string[], callback: (elem: XmlElementWrapper) => void): void
    {
        for (const subElem of this.elem.children) {
            if (childTagName.includes(subElem.tagName)) {
                callback(new XmlElementWrapper(subElem, this.langCode, this.fallbackLangCode));
            }
        }
    }

    /**
     * Calls a callback for every child element matching the given tag name.
     * 
     * @param childTagName  The tag name to filter children with
     * @param callback      A callback executed for each matching element
     */
    public forEachChildWithTag(childTagName: string, callback: (elem: XmlElementWrapper) => void): void
    {
        this.forEachChildWithTagIn([childTagName], callback);
    }

    /**
     * Returns an array containing the return values of a callback for each child element matching
     * the given tag names.
     * 
     * @param childTagName  The tag names to filter children with
     * @param callback      A callback executed for each matching element
     * @returns             An array with each element being the result of the callback for each respective child node
     */
    public mapForEachChildTagIn<T>(childTagName: string[], callback: (elem: XmlElementWrapper) => T): T[]
    {
        const data = new Array<T>();

        this.forEachChildWithTagIn(childTagName, (elem: XmlElementWrapper) => {
            data.push(callback(elem));
        });

        return data;
    }

    /**
     * Returns an array containing the return values of a callback for each child element matching
     * the given tag name.
     * 
     * @param childTagName  The tag name to filter children with
     * @param callback      A callback executed for each matching element
     * @returns             An array with each element being the result of the callback for each respective child node
     */
    public mapForEachChildTag<T>(childTagName: string, callback: (elem: XmlElementWrapper) => T): T[]
    {
        return this.mapForEachChildTagIn([childTagName], callback);
    }

    /**
     * Get the text content of the node or throw an exception if the node is empty.
     * 
     * @returns             The element's textContent
     */
    public value(): string
    {
        if (this.elem.textContent === null) {
            throw XmlDecodingException.emptyElement(this.elem);
        }

        return this.elem.textContent.trim();
    }

    /**
     * Get the text content of the node casted into a number or throw an exception if the node is empty.
     * Also throws an exception if the format is invalid.
     * 
     * @returns             The element's textContent as a number
     */
    public valueAsNumber(): number
    {
        const valueString = this.value();
        const number = Number.parseFloat(valueString);

        if (isNaN(number)) {
            throw XmlDecodingException.invalidNumber(this.elem, valueString);
        }

        return number;
    }

    /**
     * Parse the text content of the node as a date or throw an exception if the node is empty
     * or if the format is invalid.
     * Currently the server ignores the Timezone of the request, so we should add it here. We
     * use UTC as it is the same as we are sending.
     * 
     * @returns             The element's textContent as a date
     */
    public valueAsDate(): Date
    {
        let dateString = this.value();

        // If the string includes time, append UTC timezone
        if (dateString.includes("T")) {
            dateString += "Z";
        }

        const date = new Date(dateString);

        if (isNaN(date.getTime())) {
            throw XmlDecodingException.invalidDate(this.elem, this.value());
        }

        return date;
    }

    /**
     * Get the value of a child node, according to the settings of the translator.
     * If a child node matches exactly the selected locale, it will be returned. Otherwise
     * the last node with no language specified will be returned. If there is still no match,
     * the last node with the fallback language will be returned. If still nothing matches,
     * null will be returned.
     * 
     * @param langTagName   The tagName of the children nodes to consider
     * @returns             The value in the selected locale or null
     */
    public translatedElemValueOrNull(langTagName: string): string|null
    {
        let fallbackElement: Element|null = null;
        let hasFoundElemWithoutLang = false;

        for (const subElem of this.elem.children) {
            // Only consider nodes when the name matches
            if (subElem.tagName !== langTagName) {
                continue;
            }

            // If the node attribute has no lang attribute, select it as fallback
            if (!subElem.hasAttribute('lang') || subElem.getAttribute('lang') === '') {
                fallbackElement = subElem;
                hasFoundElemWithoutLang = true;
            }

            // If the lang attribute matches exactly, our translation is found
            if (subElem.getAttribute('lang') === this.langCode) {
                return subElem.innerHTML;
            }

            // Match the fallback locale, but prefer the unspecified fallback if already found one
            if (subElem.getAttribute('lang') === this.fallbackLangCode && !hasFoundElemWithoutLang) {
                fallbackElement = subElem;
            }
        }

        // If an exact lang match was not found, use the fallback element
        if (fallbackElement !== null) {
            return fallbackElement.innerHTML;
        }

        return null;
    }

    /**
     * Same as getTranslatedElementValue, but will throw an exception if the translated value
     * cannot be found.
     * 
     * @param langTagName   The tagName of the children nodes to consider
     * @returns             The value in the selected locale
     */
    public translatedElemValue(langTagName: string): string
    {
        const value = this.translatedElemValueOrNull(langTagName);

        if (value === null) {
            throw XmlDecodingException.requiredChildNotFound(this.elem, langTagName);
        }

        return value;
    }

    /**
     * Get the list of values in an element matching the selected language. Elements with
     * no language defined will also be returned.
     * If there are no values for the selected languages, values for the fallback language will
     * be returned instead.
     * 
     * @param langNodeName  The tagName of the children nodes to consider
     * @rturns              The values in the selected locale
     */
    public translatedElemValues(langNodeName: string): string[]
    {
        let hasValuesInCurrentLocale = false;

        // Browse children a first time to count how much values there are in the current locale
        for (const subElem of this.elem.children) {
            if (subElem.tagName === langNodeName && subElem.getAttribute('lang') === this.langCode) {
                hasValuesInCurrentLocale = true;
                break;
            }
        }

        // Choose whether to use the current locale or the fallback one
        const languageCode = hasValuesInCurrentLocale ? this.langCode : this.fallbackLangCode;

        // Now brows the children for real, collecting the attributes in order
        const values: string[] = [];

        for (const subElem of this.elem.children) {
            if (subElem.tagName !== langNodeName) {
                continue;
            }

            if (!subElem.hasAttribute('lang') || subElem.getAttribute('lang') === languageCode) {
                values.push(subElem.innerHTML);
            }
        }

        return values;
    }

    /**
     * Returns the text value of an XML element, typed as the value of an enum.
     * If the text value doesn't belong the the enum, a fallback value is returned.
     * 
     * @param sourceEnum    The enum to which the value should belong
     * @param fallback      The value to return if the element doesn't belong to the enum
     * @returns             The text value of elem, typed as sourceEnum, or fallback
     */
    public enumValueOrDefault<T>(sourceEnum: T, fallback: T[keyof T]): T[keyof T]
    {
        try {
            return this.enumValue(sourceEnum);
        }
        catch {
            return fallback;
        }
    }

    /**
     * Validates that the text value of an XML element is included in an enum.
     * For convenience, this value will be typed and returned. If the value
     * doesn't belong to the enum, an exception is thrown.
     * 
     * @param sourceEnum    The enum to which the value should belong
     * @returns             The text value of elem, typed as sourceEnum
     */
    public enumValue<T>(sourceEnum: T): T[keyof T]
    {
        const value: string|null = this.elem.textContent?.trim() ?? null;

        if (value === null || !Object.values(sourceEnum).includes(value)) {
            throw XmlDecodingException.notInEnum(this.elem, sourceEnum);
        }

        return value as unknown as T[keyof T];
    }

    /**
     * Get the value of an XML attribute or throws an exception
     * if this attribute doesn't exist or has no value.
     * 
     * @param attrName      The name of the attribute to search for
     */
    public attrValue(attrName: string): string
    {
        if (!this.elem.hasAttribute(attrName)) {
            throw XmlDecodingException.attributeNotFound(this.elem, attrName);
        }

        const value = this.elem.getAttribute(attrName);

        if (value === null) {
            throw XmlDecodingException.nullAttribute(this.elem, attrName);
        }

        return value;
    }
}
